import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CounselorDutyPreferencesResponse, CounselorDutyType, DayOfWeek, counselorsApi } from "@/api";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { nameof } from "@/common/helper/commonHelper";
import { useCourts } from "@/common/hooks/useCourts";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import {
  BlockedPeriodRowValues,
  CounselorDutyPreferencesFormValues,
  CourtOption,
  DutyPreferenceRowValues,
} from "../CounselorDutyPreferences.types";
import { DUTY_TYPE_LABEL_KEYS, counselorDutyPreferencesValidationSchema } from "../CounselorDutyPreferences.utils";

const WEEKDAY_ORDER: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
];

type CompleteBlockedPeriod = BlockedPeriodRowValues & { dutyType: CounselorDutyType; from: Date; to: Date };

function buildDutyPreferences(response: CounselorDutyPreferencesResponse | undefined): DutyPreferenceRowValues[] {
  return WEEKDAY_ORDER.map((weekday) => {
    const saved = response?.dutyPreferences.find((d) => d.weekday === weekday);
    return {
      weekday,
      aw12: saved?.aw12 ?? false,
      aw3: saved?.aw3 ?? false,
      bg: saved?.bg ?? false,
    };
  });
}

function buildBlockedPeriods(response: CounselorDutyPreferencesResponse | undefined): BlockedPeriodRowValues[] {
  return (response?.blockedPeriods ?? []).map((p) => ({
    id: p.id,
    dutyType: p.dutyType,
    from: p.from,
    to: p.to,
  }));
}

function isComplete(period: BlockedPeriodRowValues): period is CompleteBlockedPeriod {
  return period.dutyType !== null && period.from !== null && period.to !== null;
}

export function useCounselorDutyPreferences(counselorId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();
  const { courts } = useCourts();

  const { data } = useQuery<CounselorDutyPreferencesResponse>({
    queryKey: [QueryKey.CounselorDutyPreferences, counselorId],
    queryFn: () => counselorsApi.getCounselorDutyPreferences({ id: counselorId }),
    meta: { loadingScope: "pageContent" },
  });

  const courtOptions = useMemo<CourtOption[]>(
    () => courts.map((c) => ({ id: c.id, label: c.shortCode })).sort((a, b) => a.label.localeCompare(b.label)),
    [courts]
  );

  const dutyTypeOptions = useMemo<IOptionItem[]>(
    () =>
      Object.values(CounselorDutyType).map((dutyType) => ({ id: dutyType, label: t(DUTY_TYPE_LABEL_KEYS[dutyType]) })),
    [t]
  );

  const saveMutation = useMutation({
    mutationFn: (values: CounselorDutyPreferencesFormValues) =>
      counselorsApi.saveCounselorDutyPreferences({
        id: counselorId,
        saveCounselorDutyPreferencesRequest: {
          courtHours: values.courtHours,
          awHours: values.awHours,
          preferredCourtIds: values.preferredCourtIds,
          excludedCourtIds: values.excludedCourtIds,
          dutyPreferences: values.dutyPreferences.map((d) => ({
            weekday: d.weekday,
            aw12: d.aw12,
            aw3: d.aw3,
            bg: d.bg,
          })),
          blockedPeriods: values.blockedPeriods.filter(isComplete).map((p) => ({
            id: p.id ?? null,
            dutyType: p.dutyType,
            from: p.from,
            to: p.to,
          })),
        },
      }),
  });

  const validationSchema = useMemo(() => counselorDutyPreferencesValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<CounselorDutyPreferencesFormValues>({
    initialValues: {
      preferredCourtIds: data?.preferredCourtIds ?? [],
      excludedCourtIds: data?.excludedCourtIds ?? [],
      courtHours: data?.courtHours ?? null,
      awHours: data?.awHours ?? null,
      dutyPreferences: buildDutyPreferences(data),
      blockedPeriods: buildBlockedPeriods(data),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await saveMutation.mutateAsync(values);
      showSuccess(t("counselors.dutyPreferences.saveSuccess"));
      void queryClient.invalidateQueries({ queryKey: [QueryKey.CounselorDutyPreferences, counselorId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Counselors] });
    },
  });

  const handleAddBlockedPeriod = () => {
    void formik.setFieldValue(nameof<CounselorDutyPreferencesFormValues>("blockedPeriods"), [
      ...formik.values.blockedPeriods,
      { dutyType: null, from: null, to: null },
    ]);
  };

  const handleDeleteBlockedPeriod = (index: number) => {
    void formik.setFieldValue(
      nameof<CounselorDutyPreferencesFormValues>("blockedPeriods"),
      formik.values.blockedPeriods.filter((_, i) => i !== index)
    );
  };

  return {
    formik,
    courtOptions,
    dutyTypeOptions,
    handleAddBlockedPeriod,
    handleDeleteBlockedPeriod,
  };
}
